import { NodeSelectionChange, applyEdgeChanges, applyNodeChanges } from 'reactflow';
import {
    actionNodeTypes,
    triggerNodeTypes,
} from 'assets/js/pages/engage/workflows/utils/constants';
import { buildEdges } from 'assets/js/pages/engage/workflows/utils/helpers';
import {
    AutoSaveStatus,
    NodeWithData,
    ReactFlowState,
} from 'assets/js/pages/engage/workflows/utils/types';
import { WorkflowUtilityNodeTypeEnum } from 'constants/workflows';
import { reject } from 'lodash';
import { v4 as uuid } from 'uuid';
import { createWithEqualityFn as create } from 'zustand/traditional';

const MAX_HISTORY_SIZE = 100;

const initialState = {
    nodes: [],
    edges: [],
    shouldLayout: false,
    lastSelectedNodeId: null,
    past: [],
    future: [],
    canUndo: false,
    canRedo: false,
    autoSaveStatus: 'success' as AutoSaveStatus,
    lastAddedId: null,
};

const useReactFlowStore = create<ReactFlowState>((set, get) => ({
    ...initialState,
    onNodesChange: changes => {
        set({
            // the type assertion here is due to https://github.com/xyflow/xyflow/issues/3030
            nodes: applyNodeChanges(changes, get().nodes) as NodeWithData[],
        });
        if (changes.some(c => c.type === 'dimensions')) {
            set({ shouldLayout: true });
        }

        if (changes.some(c => c.type === 'select')) {
            // keep track of the last node selected, to help with our call to `fitView()`
            get().setLastSelectedNodeId({ changes });

            // remove choose_action node on deselect if we already have an action
            get().deselectChooseAction(changes);
        }
    },
    onEdgesChange: changes => {
        set({
            edges: applyEdgeChanges(changes, get().edges),
        });
    },
    setNodes: (nodes, options) => {
        const { setEdges = true } = options || {};
        set({ nodes, edges: setEdges ? buildEdges(nodes) : get().edges });
    },
    setEdges: edges => set({ edges }),
    getNodes: () => get().nodes,
    getEdges: () => get().edges,
    getNode: nodeId => get().nodes.find(node => node.id === nodeId),
    getEdge: edgeId => get().edges.find(edge => edge.id === edgeId),
    updateNode: (nodeId, newNode) => {
        set({
            nodes: get().nodes.map(node => (node.id === nodeId ? newNode : node)),
        });
    },
    updateEdge: (edgeId, newEdge) => {
        set({
            edges: get().edges.map(edge => (edge.id === edgeId ? newEdge : edge)),
        });
    },
    setShouldLayout: shouldLayout => set({ shouldLayout }),
    takeSnapshot: () => {
        const { past, nodes } = get();
        const nodesArePositioned = nodes.every(n => !!n.position.x);

        if (nodesArePositioned) {
            set({
                past: [...past.slice(past.length - MAX_HISTORY_SIZE + 1, past.length), { nodes }],
                future: [],
            });
        }
    },
    undo: () => {
        const { past, nodes, future } = get();
        const pastState = past[past.length - 1];

        if (pastState) {
            set({
                past: past.slice(0, past.length - 1),
                future: [...future, { nodes }],

                nodes: pastState.nodes,
                edges: buildEdges(pastState.nodes),
                shouldLayout: true,
            });
        }
    },
    redo: () => {
        const past = get().past;
        const future = get().future;
        const nodes = get().nodes;

        const futureState = future[future.length - 1];

        if (futureState) {
            set({
                future: future.slice(0, future.length - 1),
                past: [...past, { nodes }],

                nodes: futureState.nodes,
                edges: buildEdges(futureState.nodes),
                shouldLayout: true,
            });
        }
    },
    reset: () => set(initialState),
    deselectChooseAction: changes => {
        const nodes = get().nodes;
        const edges = get().edges;
        const actions = nodes.filter(actionNodeFilter);
        const change = changes.find(c => c.type === 'select' && c.id) as NodeSelectionChange;
        const node = nodes.find(n => n.id === change.id);

        if (
            change.selected === false &&
            node?.type === WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION &&
            actions.length > 0
        ) {
            const topEdge = edges.find(edge => edge.target === node.id);
            const bottomEdge = edges.find(edge => edge.source === node.id);

            let newNodes: NodeWithData[] = [...nodes.filter(n => n.id !== node.id)];

            // if we have both a top and bottom edge, make sure to connect the two nodes again
            if (topEdge && bottomEdge) {
                newNodes = newNodes.map(node => {
                    if (node.id !== bottomEdge.target || !node.data) return node;

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            parentId: topEdge.source,
                        },
                    };
                });
            }

            // if we only have a top edge, we need to insert an LEAF_END node and connect it
            if (topEdge && !bottomEdge) {
                const addStepNode: NodeWithData = {
                    id: uuid(),
                    type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                    data: {
                        type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                        parentId: topEdge.source,
                    },
                    position: { x: 0, y: 0 },
                    style: { opacity: 0 },
                };
                newNodes.push(addStepNode);
            }
            const newEdges = buildEdges(newNodes);

            set({
                nodes: newNodes,
                edges: newEdges,
                shouldLayout: true,
            });
        }
    },
    setLastSelectedNodeId: ({ id, changes }) => {
        if (id) {
            set({ lastSelectedNodeId: id });
            return;
        }

        if (changes?.length === 1) {
            const change = changes[0];
            if (change.type === 'select' && change.id) {
                set({ lastSelectedNodeId: change.id });
            }
            return;
        }

        if (changes?.length && changes.length > 1) {
            for (const change of changes) {
                if (change.type === 'select' && change.selected) {
                    set({ lastSelectedNodeId: change.id });
                    break;
                }
            }
        }
    },
    setAutoSaveState: status => {
        switch (status) {
            case 'pending':
                set({ autoSaveStatus: 'pending' });
                break;
            case 'error':
                set({ autoSaveStatus: 'error' });
                break;
            case 'success':
                set({ autoSaveStatus: 'success' });
                break;
        }
    },
    setLastAddedId: id => set({ lastAddedId: id }),
}));

const historySelector = (state: ReactFlowState) => {
    return {
        canUndo: state.past.length > 0,
        canRedo: state.future.length > 0,
        past: state.past,
        future: state.future,
        takeSnapshot: state.takeSnapshot,
        undo: state.undo,
        redo: state.redo,
    };
};

const nodesInitializedSelector = (state: ReactFlowState) =>
    state.nodes.every(node => node.width && node.height);

const shouldLayoutSelector = (state: ReactFlowState) =>
    state.nodes.every(node => node.width && node.height) && state.shouldLayout;

const totalHeightSelector = (state: ReactFlowState) => {
    const lastNode = state.nodes.at(-1);
    if (!lastNode) return 0;

    return lastNode.position.y + (lastNode.height ?? 0);
};

const nodeFormErrorsSelector = (state: ReactFlowState) => {
    const nodesWithError = reject(
        state.nodes,
        node => Object.keys(node.data?.formData?.errors || {}).length === 0,
    );
    return {
        errors: nodesWithError,
        errorCount: nodesWithError.length,
    };
};

const actionNodesSelector = (state: ReactFlowState) => state.nodes.filter(actionNodeFilter);

const isFormDirtySelector = (state: ReactFlowState) =>
    state.nodes.some(n => n.data?.formData?.isDirty);

const workflowHasActionSelector = (state: ReactFlowState) => {
    return workflowHasAction(state.nodes);
};

const actionsHaveFormDataSelector = (state: ReactFlowState) => {
    return state.nodes.filter(actionNodeFilter).every(n => !!n.data?.formData);
};

const autoSavedSelector = (state: ReactFlowState) => {
    return {
        autoSaveStatus: state.autoSaveStatus,
        setAutoSaveState: state.setAutoSaveState,
    };
};

const workflowHasAction = (nodes: NodeWithData[]) => {
    const hasTriggerNode = nodes.some(triggerNodeFilter);
    const hasActionNode = nodes.some(actionNodeFilter);

    return hasTriggerNode && hasActionNode;
};

const workflowHasBranch = (nodes: NodeWithData[]) => {
    return nodes.some(n => n.type === WorkflowUtilityNodeTypeEnum.BRANCH);
};

const actionNodeFilter = (n: NodeWithData) => n.type && actionNodeTypes.includes(n.type);
const triggerNodeFilter = (n: NodeWithData) => n.type && triggerNodeTypes.includes(n.type);

const allSelector = ({
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    setNodes,
    setEdges,
    getNodes,
    getEdges,
    updateNode,
    updateEdge,
    reset,
    shouldLayout,
    setShouldLayout,
    past,
    future,
    takeSnapshot,
    undo,
    redo,
    autoSaveStatus,
    setAutoSaveState,
}: ReactFlowState) => ({
    nodes,
    edges,
    shouldLayout,
    onNodesChange,
    onEdgesChange,
    setNodes,
    setEdges,
    getNodes,
    getEdges,
    updateNode,
    updateEdge,
    reset,
    setShouldLayout,
    past,
    future,
    takeSnapshot,
    undo,
    redo,
    autoSaveStatus,
    setAutoSaveState,
});

export {
    useReactFlowStore,
    allSelector,
    nodesInitializedSelector,
    shouldLayoutSelector,
    totalHeightSelector,
    nodeFormErrorsSelector,
    actionNodesSelector,
    historySelector,
    isFormDirtySelector,
    workflowHasActionSelector,
    actionsHaveFormDataSelector,
    workflowHasAction,
    workflowHasBranch,
    autoSavedSelector,
    actionNodeFilter,
    triggerNodeFilter,
};
